import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next';
import { 
  Save, Trash2, ArrowLeft, Plus, Search, FileText, 
  Tag, Paperclip, History, Zap, ChevronLeft, ChevronRight as ChevronRightIcon, 
  Workflow, Type, Minus, Info, User, Phone, Mail, Activity, ShieldAlert, Clock, RefreshCw, List,
  BrainCircuit, Sparkles, Wrench, Cpu // 🎯 أيقونات الـ AI الجديدة
} from 'lucide-react';

import UniversalLookup from './UniversalLookup';
import StatusModal from './StatusModal';
import StatusHistoryModal from './StatusHistoryModal';
import AttachmentsModal from './AttachmentsModal';
import AuditHistoryModal from './AuditHistoryModal';
import WorkflowActionModal from './WorkflowActionModal';
import WorkflowHistoryModal from './WorkflowHistoryModal';
import SmartPrintButton from './PrintButton'; 
import { useFontSize } from './FontSizeContext';
import { usePermissions } from './usePermissions';
import CustomFieldSlot from './CustomFieldSlot';

const SRDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isNew = !id || id === 'new';
  
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language.startsWith('ar') || i18n.language.startsWith('ur');

  const fontContext = useFontSize();
  const fontSize = fontContext?.fontSize || 13;
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('MAIN');
  const { canCreate, canUpdate, canDelete } = usePermissions('sr');
  
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [isDocModalOpen, setIsDocModalOpen] = useState(false);
  const [isWFHistoryOpen, setIsWFHistoryOpen] = useState(false);
  const [showAudit, setShowAudit] = useState(false);
  const [wfContext, setWfContext] = useState({ can_start: false, can_action: false });
  const [relatedWO, setRelatedWO] = useState([]);
  
  const session = JSON.parse(localStorage.getItem('user') || '{}');
  const sessionSite = session.site_id || '';
  const sessionOrg = session.org_id || '';  

  // 🎯 ضفنا حقول الـ AI الجديدة للـ State
  const [data, setData] = useState({
    srnum: '', description: '', long_description: '', status: 'NEW',
    reportedby: '', phone: '', email: '', assetnum: '', location: '',
    priority: 3, site_id: sessionSite, org_id: sessionOrg, reportdate: '',
    work_type: '', target_craft: '', ai_summary: '', ai_parts: []
  });
  
  const [count, setCount] = useState(0); 
  const [nav, setNav] = useState({ first: null, last: null, next: null, prev: null, total: 0, current_pos: 0 });
  const [lookup, setLookup] = useState({ isOpen: false, title: '', type: '', field: '' });
  
  // 🎯 State خاصة بأنيميشن الـ AI
  const [isAiThinking, setIsAiThinking] = useState(false);
  const [aiHighlight, setAiHighlight] = useState(false);

  const isReadOnly = ['CLOSED', 'CAN', 'RESOLVED', 'CLOSE'].includes(data.status);

  // ... (نفس دوال الـ loadData, loadNavigation, fetchCount, handleSave, handleDelete ... تفضل زي ما هي بدون تغيير)

  const loadData = useCallback(async () => {
    if (!isNew) {
      try {
        setLoading(true);
        const res = await axios.get(`/api/sr/${id}`);
        setData(res.data);
      } catch (err) { toast.error(t('common.load_error', "Failed to load SR data")); }
      finally { setLoading(false); }
    }
  }, [id, isNew, t]);

  useEffect(() => { loadData(); }, [loadData]);

  useEffect(() => {
    const openLog = () => setShowAudit(true);
    window.addEventListener('GLOBAL_LOG_HANDLER', openLog);
    return () => window.removeEventListener('GLOBAL_LOG_HANDLER', openLog);
  }, []);

  useEffect(() => {
    if (isNew && data.org_id) {
        setData(prev => ({
          ...prev, description: '', status: 'NEW', site_id: sessionSite, org_id: sessionOrg,
          reportedby: session.username || '', phone: '', email: '', assetnum: '', location: '', priority: 3,
          reportdate: new Date().toISOString().split('T')[0]
        }));         
        axios.get(`/api/sr/next-number/${data.org_id}`)
            .then(res => setData(prev => ({ ...prev, srnum: res.data.next_srnum || '' })));
    }
  }, [isNew, data.org_id, sessionSite, sessionOrg, session.username]);

  const loadNavigation = useCallback(async () => {
    if (isNew) return;
    try {
      const token = localStorage.getItem('token'); 
      const res = await axios.get(`/api/sr/${id}/navigation${location.search}`, { headers: { 'Authorization': `Bearer ${token}` } });
      setNav(res.data || { first: null, last: null, next: null, prev: null, total: 0, current_pos: 0 });
    } catch (err) { console.error("Nav failed", err); }
  }, [id, isNew, location.search]);

  useEffect(() => { loadNavigation(); }, [loadNavigation]);

  const fetchCount = useCallback(async () => {
    if (isNew || !data.srnum) return;
    try {
      const res = await axios.get(`/api/attachments/count/SR/${data.srnum}`);
      setCount(res.data.count || 0);
    } catch (err) { console.error("Attach count error", err); }
  }, [data.srnum, isNew]);

  useEffect(() => { fetchCount(); }, [fetchCount]);

  const handleSave = async () => {
    const loadId = toast.loading(t('common.saving', "Saving..."));
    try {
      setLoading(true);
      const token = localStorage.getItem('token'); 
      const res = await axios.post('/api/sr/save', data, { headers: { 'Authorization': `Bearer ${token}` } });
      toast.success(t('common.save_success', "SR Saved Successfully!"), { id: loadId });
      if (isNew) navigate(`/sr/${res.data.srnum}${location.search}`);
    } catch (err) { 
      toast.error(err.response?.data?.detail || t('common.save_fail', "System Error"), { id: loadId });
    } finally { setLoading(false); }
  };

  const handleDelete = async () => {
    if (data.status !== 'NEW') return Swal.fire({ title: t('common.error'), text: t('sr.details.delete_new_only'), icon: 'error' });
    const result = await Swal.fire({
        title: t('common.are_you_sure'), text: t('sr.details.delete_warn'),
        icon: 'warning', showCancelButton: true, confirmButtonColor: '#d33',
        confirmButtonText: t('common.yes_delete'),
        customClass: { popup: `${isRTL ? 'font-cairo' : 'font-sans'} text-[12px]` }
    });
    if (result.isConfirmed) {
        try {
            const token = localStorage.getItem('token');
            await axios.delete(`/api/sr/${id}`, { headers: { 'Authorization': `Bearer ${token}` } });
            toast.success(t('common.delete_success'));
            navigate(`/sr${location.search}`);
        } catch (err) { toast.error(err.response?.data?.detail || t('common.delete_fail')); }
    }
  };

  useEffect(() => {
    const triggerDelete = () => handleDelete();
    window.addEventListener('GLOBAL_DELETE_HANDLER', triggerDelete);
    return () => window.removeEventListener('GLOBAL_DELETE_HANDLER', triggerDelete);
  }, [handleDelete]);

  const fetchWFStatus = async () => {
    if (isNew) return;
    try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`/api/workflow/check-runtime/SR/${id}`, { headers: { 'Authorization': `Bearer ${token}` } });
        setWfContext(res.data);
    } catch (err) { console.error("WF Check Failed", err); }
  };

  useEffect(() => { fetchWFStatus(); }, [id, data.status]);

  const handleRouteWorkflow = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post('/api/workflow/initiate', { module: 'SR', ref_id: id }, { headers: { 'Authorization': `Bearer ${token}` } });
      toast.success(t('common.wf_initiated'));
      fetchWFStatus(); loadData();
    } catch (err) { toast.error(t('common.wf_fail')); }
  };

  const handleLookupSelect = (row) => {
    // 🎯 استخراج القيمة بناءً على نوع اللوكاب
    const val = row.personid || row.assetnum || row.location_id || row.value || row.itemnum;
    
    // 🎯 تحديث الداتا
    setData(prev => ({ ...prev, [lookup.field]: row.personid ? row.personid : val }));
    
    // 🎯 قفل الـ Lookup (الفلترات مش محتاجين نحدثها هنا، هي اتطبقت وقت الفتح)
    setLookup(prev => ({ ...prev, isOpen: false }));
  };

  const goTo = (target) => target && navigate(`/sr/${target}${location.search}`);

  const fetchRelatedRecords = useCallback(async () => {
    if (isNew || !data.srnum) return;
    try {
      const res = await axios.get(`/api/sr/${data.srnum}/related-wo`);
      setRelatedWO(res.data || []);
    } catch (err) { console.error("Failed to fetch related WO", err); }
  }, [data.srnum, isNew]);

  useEffect(() => { if (activeTab === 'RELATED') fetchRelatedRecords(); }, [activeTab, fetchRelatedRecords]);

  const formatDateForInput = (dateString) => dateString ? dateString.split('T')[0] : "";


  // =========================================================================
  // 🧠 ✨ السحر يبدأ هنا: دالة النداء لمحرك الذكاء الاصطناعي
  // =========================================================================
  const handleSmartTriage = async () => {
    const issueText = data.long_description || data.description;
    if (!issueText) {
      toast.error(t('sr.ai.no_text', 'Please write the problem description first so the AI can analyze it.'));
      return;
    }

    setIsAiThinking(true);
    try {
      const token = localStorage.getItem('token');
      
      // 🎯 تحديد اسم اللغة الصريح بناءً على اختيار المستخدم في الواجهة
      let currentLangName = "English";
      if (i18n.language.startsWith('ar')) currentLangName = "Arabic";
      else if (i18n.language.startsWith('ur')) currentLangName = "Urdu";
      else if (i18n.language.startsWith('es')) currentLangName = "Spanish";
      else if (i18n.language.startsWith('fr')) currentLangName = "French";
      else if (i18n.language.startsWith('de')) currentLangName = "German";
      else if (i18n.language.startsWith('zh')) currentLangName = "Simplified Chinese (Mandarin / 中文)";
      else if (i18n.language.startsWith('ko')) currentLangName = "Korean (한국어)";
      else if (i18n.language.startsWith('ja')) currentLangName = "Japanese (日本語)";
      else if (i18n.language.startsWith('ru')) currentLangName = "Russian";
      else if (i18n.language.startsWith('it')) currentLangName = "Italian";

      const payload = {
        description: issueText,
        asset_name: data.assetnum || "General Facility",
        org_id: data.org_id,
        lang: currentLangName // 🎯 إرسال اللغة هنا
      };

      const res = await axios.post('/api/smart-triage', payload, { headers: { 'Authorization': `Bearer ${token}` } });
      
      const aiData = res.data.triage_result;
      console.log("AI Triage Result:", aiData);

      setData(prev => ({
        ...prev,
        priority: aiData.priority || prev.priority,
        work_type: aiData.work_type || prev.work_type,
        target_craft: aiData.target_craft || prev.target_craft,
        ai_summary: aiData.technical_summary,
        ai_parts: aiData.suggested_parts || []
      }));

      setAiHighlight(true);
      setTimeout(() => setAiHighlight(false), 2500);

      toast.success(t('sr.ai.success', 'AI Triage Complete! Fields auto-filled.'));

    } catch (err) {
      console.error(err);
      toast.error(t('sr.ai.fail', 'AI Engine is currently unavailable.'));
    } finally {
      setIsAiThinking(false);
    }
  };
  // =========================================================================

  if (loading && !data.srnum) return <div className="h-screen flex items-center justify-center bg-surface-panel"><RefreshCw className="animate-spin text-primary" /></div>;

  return (
    <div className={`flex flex-col h-screen bg-surface-app tracking-tighter uppercase ${isRTL ? 'font-cairo' : 'font-sans'}`} style={{ fontSize: `${fontSize}px` }} dir={isRTL ? 'rtl' : 'ltr'}>
      <UniversalLookup {...lookup} onClose={() => setLookup({ ...lookup, isOpen: false })} onSelect={handleLookupSelect} />

      {/* --- TOP TOOLBAR --- */}
      <div className="bg-primary-dark text-white p-1.5 flex justify-between items-center shadow-lg z-30 px-4 shrink-0">
        <div className={`flex items-center gap-3 ${isRTL ? 'mr-2' : 'ml-2'}`}>
          <button onClick={() => navigate(`/sr${location.search}`)} className="p-1.5 hover:bg-white/10 rounded-full text-white/70">
            {isRTL ? <ChevronRightIcon size={18} /> : <ArrowLeft size={18} />}
          </button>
          {!isNew && (
            <div className={`flex items-center bg-black/20 border border-white/10 rounded-md px-1 py-0.5 gap-1 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <button disabled={!nav.prev} onClick={() => goTo(nav.first)} className="p-1 hover:text-status-warning disabled:opacity-30"><ChevronLeft size={16} strokeWidth={3} className={isRTL ? 'rotate-180' : ''}/></button>
              <button disabled={!nav.prev} onClick={() => goTo(nav.prev)} className="p-1 hover:text-status-warning disabled:opacity-30"><ChevronLeft size={16} className={isRTL ? 'rotate-180' : ''}/></button>
              <div className="px-2 text-[10px] font-mono border-x border-white/10 mx-1"><span className="text-status-warning">{nav.current_pos}</span> / {nav.total}</div>
              <button disabled={!nav.next} onClick={() => goTo(nav.next)} className="p-1 hover:text-status-warning disabled:opacity-30"><ChevronRightIcon size={16} className={isRTL ? 'rotate-180' : ''}/></button>
              <button disabled={!nav.next} onClick={() => goTo(nav.last)} className="p-1 hover:text-status-warning disabled:opacity-30"><ChevronRightIcon size={16} strokeWidth={3} className={isRTL ? 'rotate-180' : ''}/></button>
            </div>
          )}
        </div>

        <div className={`flex items-center gap-2 ${isRTL ? 'ml-2' : 'mr-2'}`}>
          
          {/* 🧠 زرار الـ AI في التول بار */}
          <button 
            onClick={handleSmartTriage} 
            disabled={isAiThinking || isReadOnly} 
            className={`flex items-center gap-2 px-4 py-1.5 rounded-sm text-[11px] font-black shadow-md transition-all ${isAiThinking ? 'bg-indigo-400 cursor-not-allowed' : 'bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white'}`}
          >
            {isAiThinking ? <RefreshCw size={14} className="animate-spin" /> : <BrainCircuit size={14} />} 
            {isAiThinking ? t('sr.ai.thinking', 'ANALYZING...') : t('sr.ai.btn', 'AI AUTO-TRIAGE')}
          </button>

          <div className="h-6 w-[1px] bg-white/10 mx-1"></div>

          {((isNew && canCreate) || (!isNew && canUpdate)) && (
          <button onClick={handleSave} disabled={loading || isReadOnly} className="bg-status-success hover:bg-status-success text-white px-4 py-1.5 rounded-sm flex items-center gap-2 text-[11px] font-black shadow-md transition-all">
            <Save size={14} /> {loading ? t('common.saving') : t('common.save')}
          </button>
          )}

          {wfContext.can_start && !isReadOnly && (
            <button onClick={handleRouteWorkflow} className="bg-status-warning hover:bg-status-warning text-white flex items-center gap-2 text-[11px] font-black h-8 shadow-md px-4 rounded-sm transition-all">
              <Zap size={14} fill="currentColor" /> {t('common.route_wf', 'ROUTE WORKFLOW')}
            </button>
          )}

          <WorkflowActionModal moduleName="SR" refId={id} currentStatus={data.status} onActionComplete={loadData} />
          <button onClick={() => setIsWFHistoryOpen(true)} className="p-2 hover:bg-white/10 rounded text-white/70" title={t('common.wf_history')}><Workflow size={16} className="text-status-warning"/></button>
          <div className="h-6 w-[1px] bg-white/10 mx-1"></div>
          <SmartPrintButton moduleName="SR" refId={`${data.srnum}`} />
          <button onClick={() => setShowStatusModal(true)} disabled={isReadOnly} className="p-2 hover:bg-white/10 rounded text-white/70" title={t('common.change_status')}><Tag size={16}/></button>

          <button onClick={() => setIsDocModalOpen(true)} className="relative p-2 hover:bg-white/10 rounded text-white/70" title={t('common.attachments')}>
            <Paperclip size={16} />
            {count > 0 && <span className="absolute top-1 right-1 bg-status-danger text-[8px] w-3.5 h-3.5 rounded-full flex items-center justify-center font-black">{count}</span>}
          </button>

          <button onClick={() => setShowHistoryModal(true)} className="p-2 hover:bg-white/10 rounded text-white/70" title={t('common.status_history')}><History size={16}/></button>

          {!isNew && canDelete && (
          <button onClick={handleDelete} disabled={isReadOnly} className="p-2 hover:text-status-danger rounded text-white/70" title={t('common.delete')}><Trash2 size={16}/></button>
          )}

          {/* Font Controls */}
          <div className={`flex items-center gap-2 bg-white/10 px-3 py-1 rounded-full border border-white/20 ${isRTL ? 'mr-4' : 'ml-4'}`}>
            <button onClick={() => fontContext.changeFontSize(-1)} className="hover:text-status-warning"><Minus size={14} /></button>
            <div className="flex items-center gap-1 min-w-[40px] justify-center text-white/70">
              <Type size={14} /><span className="font-black text-[10px]">{fontSize}</span>
            </div>
            <button onClick={() => fontContext.changeFontSize(1)} className="hover:text-status-warning"><Plus size={14} /></button>
          </div>
        </div>
      </div>

      {/* --- HEADER FIELDS BAR --- */}
      <div className="bg-surface-card border-b border-line px-6 py-3 flex items-center gap-12 shadow-sm shrink-0 z-20">
        <HeaderField label={t('sr.fields.srnum')} value={data.srnum} isBold width="w-32" disabled={isReadOnly} onChange={v => setData({...data,srnum: v})} isRTL={isRTL} />
        <HeaderField label={t('assets.headers.desc')} value={data.description} onChange={v => setData({...data, description: v})} width="w-[450px]" disabled={isReadOnly} isRTL={isRTL} />
        <HeaderField label={t('assets.headers.status')} value={data.status} disabled width="w-24" isRTL={isRTL} />
        <HeaderField label={t('common.site')} value={data.site_id} disabled width="w-24" isRTL={isRTL} />
      </div>

      {/* --- TABS --- */}
      <div className="flex bg-surface-card border-b border-line-subtle px-4 shrink-0 font-black uppercase tracking-tighter">
        {['MAIN', 'RELATED'].map(tkey => (
          <button key={tkey} onClick={() => setActiveTab(tkey)}
            className={`px-8 py-3 flex items-center gap-2 transition-all
            ${activeTab === tkey ? 'border-b-2 border-primary-dark text-primary-dark' : 'text-ink-secondary hover:text-ink'}`}>
            {tkey === 'MAIN' ? <FileText size={13}/> : <List size={13}/>} {t(`sr.tabs.${tkey.toLowerCase()}`)}
          </button>
        ))}
      </div>

      {/* --- CONTENT AREA --- */}
      <div className="flex-1 overflow-y-auto p-4 bg-surface-panel custom-scrollbar">
        {activeTab === 'MAIN' && (
          <div className="grid grid-cols-12 gap-6 max-w-7xl mx-auto animate-in fade-in duration-500 pb-20">
            {/* Left Column */}
            <div className="col-span-8 space-y-6">
              <SectionCard title={t('sr.sections.requester')} isRTL={isRTL}>
                <div className="grid grid-cols-2 gap-x-12 gap-y-4">
                  <EditableField label={t('sr.fields.reportedby')} value={data.reportedby} icon={<User size={14}/>} onSearch={() => setLookup({isOpen: true, title: t('sr.lookup.person'), type: 'persons', field: 'reportedby'})} onChange={v => setData({...data, reportedby: v})} disabled={isReadOnly} isRTL={isRTL}/>
                  <EditableField label={t('sr.fields.phone')} value={data.phone} icon={<Phone size={14}/>} onChange={v => setData({...data, phone: v})} disabled={isReadOnly} isRTL={isRTL}/>
                  <EditableField label={t('sr.fields.email')} value={data.email} icon={<Mail size={14}/>} onChange={v => setData({...data, email: v})} disabled={isReadOnly} isRTL={isRTL}/>
                  <EditableField label={t('sr.fields.reportdate')} value={formatDateForInput(data.reportdate)} disabled={isReadOnly} type="date" onChange={v => setData({...data, reportdate: v})} isRTL={isRTL}/>
                  <CustomFieldSlot coreModelName="SR" tab="MAIN" section="Requester" recordId={data.srnum} disabled={isNew} />
                </div>
              </SectionCard>

              <SectionCard title={t('sr.sections.problem')} isRTL={isRTL}>
                <div className="grid grid-cols-2 gap-x-12 gap-y-4 relative">
                  
                  {/* ✨ زرار صغير إضافي جوه الكارد نفسه */}
                  <button 
                    onClick={handleSmartTriage}
                    disabled={isAiThinking || isReadOnly}
                    className={`absolute top-0 ${isRTL ? 'left-0' : 'right-0'} bg-indigo-50 text-indigo-600 hover:bg-indigo-100 px-3 py-1.5 rounded-bl-lg flex items-center gap-1.5 text-[9px] font-black transition-all`}
                  >
                    <Sparkles size={12} className={isAiThinking ? "animate-spin" : "animate-pulse"}/> {t('sr.ai.suggest', 'AI SUGGEST')}
                  </button>
                  <EditableField 
                    label={t('sr.fields.asset')} 
                    value={data.assetnum} 
                    onSearch={() => setLookup({
                        isOpen: true, 
                        title: t('sr.lookup.asset'), 
                        type: 'assets', 
                        field: 'assetnum',
                        filters: { site_id: data.site_id, org_id: data.org_id } 
                    })} 
                    disabled={isReadOnly} 
                    isRTL={isRTL}
                  />
                  <EditableField label={t('sr.fields.location')} value={data.location_id} onSearch={() => setLookup({isOpen: true, title: t('sr.lookup.location'), type: 'locations', field: 'location'})} disabled={isReadOnly} isRTL={isRTL}/>
                  <div className="col-span-2 pt-2 relative">
                    <label className="text-[8px] text-ink-secondary font-black uppercase mb-1 italic tracking-widest">{t('sr.fields.long_desc')}</label>
                    <textarea
                      className={`w-full border rounded-sm p-3 text-[12px] font-bold text-primary bg-surface-panel outline-none focus:border-indigo-500 min-h-[120px] transition-all ${isAiThinking ? 'opacity-50' : ''}`}
                      value={data.long_description || ''}
                      disabled={isReadOnly}
                      onChange={e => setData({...data, long_description: e.target.value})}
                      placeholder={t('sr.fields.desc_placeholder', 'Describe the issue clearly...')}
                    />
                  </div>
                  <CustomFieldSlot coreModelName="SR" tab="MAIN" section="Problem" recordId={data.srnum} disabled={isNew} />
                </div>
              </SectionCard>

              {/* 🧠 ✨ كارت المخرجات الخاصة بالـ AI (بيظهر بس لو فيه داتا) */}
              {data.ai_summary && (
                <div className="bg-gradient-to-br from-indigo-900 to-purple-900 rounded-lg shadow-xl p-6 text-white animate-in slide-in-from-bottom-4 duration-500 relative overflow-hidden">
                  <div className="absolute top-0 right-0 p-8 opacity-10"><Cpu size={120} /></div>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 bg-white/20 rounded-lg backdrop-blur-sm"><BrainCircuit size={20} className="text-indigo-200"/></div>
                    <div>
                      <h3 className="font-black tracking-widest text-sm text-indigo-100">{t('sr.ai.insight_title', 'AI TECHNICAL INSIGHTS')}</h3>
                      <p className="text-[9px] text-indigo-300 font-bold uppercase">{t('sr.ai.insight_sub', 'Auto-generated analysis based on request description')}</p>
                    </div>
                  </div>
                  
                  <div className="bg-black/20 rounded-lg p-4 border border-white/10 mb-4 backdrop-blur-sm">
                    <p className="text-[11px] font-bold leading-relaxed text-indigo-50">{data.ai_summary}</p>
                  </div>

                  {data.ai_parts && data.ai_parts.length > 0 && (
                    <div>
                      <h4 className="text-[10px] font-black text-indigo-300 mb-2 flex items-center gap-1.5"><Wrench size={12}/> {t('sr.ai.parts', 'PROBABLE PARTS REQUIRED:')}</h4>
                      <div className="flex flex-wrap gap-2">
                        {data.ai_parts.map((part, idx) => (
                          <span key={idx} className="bg-indigo-500/30 border border-indigo-400/50 px-3 py-1 rounded-full text-[9px] font-black">
                            {part}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Right Column (Logistics & Classification) */}
            <div className="col-span-4 space-y-6">
              <SectionCard title={t('sr.sections.logistics')} isRTL={isRTL}>
                <div className="space-y-4">
                  {/* 🎯 الحقول اللي הـ AI هيعبيها (واخدة تأثير Highlight) */}
                  <div className={`flex flex-col border-b border-line-subtle py-1 px-1 transition-all duration-1000 ${aiHighlight ? 'bg-indigo-50 border-indigo-200 px-2 rounded' : ''}`}>
                    <label className={`text-[8px] font-black uppercase italic tracking-widest ${aiHighlight ? 'text-indigo-500' : 'text-ink-secondary'}`}>{t('sr.fields.priority')}</label>
                    <select
                      className={`font-black text-primary bg-transparent outline-none py-1 cursor-pointer ${isRTL ? 'text-right' : 'text-left'}`}
                      value={data.priority}
                      disabled={isReadOnly}
                      onChange={e => setData({...data, priority: e.target.value})}
                    >
                      <option value={1}>{t('sr.fields.priority_1', '1 - EMERGENCY (IMMEDIATE)')}</option>
                      <option value={2}>{t('sr.fields.priority_2', '2 - HIGH (SAME DAY)')}</option>
                      <option value={3}>{t('sr.fields.priority_3', '3 - MEDIUM (72 HOURS)')}</option>
                      <option value={4}>{t('sr.fields.priority_4', '4 - LOW (PLANNED)')}</option>
                      <option value={5}>{t('sr.fields.priority_5', '5 - VERY LOW (SCHEDULED)')}</option>
                    </select>
                  </div>

                  <div className={`flex flex-col border-b border-line-subtle py-1 px-1 transition-all duration-1000 delay-100 ${aiHighlight ? 'bg-indigo-50 border-indigo-200 px-2 rounded' : ''}`}>
                    <label className={`text-[8px] font-black uppercase italic tracking-widest ${aiHighlight ? 'text-indigo-500' : 'text-ink-secondary'}`}>{t('sr.fields.work_type', 'WORK TYPE')}</label>
                    <input
                      className="font-black text-primary bg-transparent outline-none py-1 w-full text-[11px]"
                      value={data.work_type || ''}
                      placeholder="e.g., CM, EM, PM"
                      disabled={isReadOnly}
                      onChange={e => setData({...data, work_type: e.target.value})}
                    />
                  </div>

                  <div className={`flex flex-col border-b border-line-subtle py-1 px-1 transition-all duration-1000 delay-200 ${aiHighlight ? 'bg-indigo-50 border-indigo-200 px-2 rounded' : ''}`}>
                    <label className={`text-[8px] font-black uppercase italic tracking-widest ${aiHighlight ? 'text-indigo-500' : 'text-ink-secondary'}`}>{t('sr.fields.target_craft', 'TARGET CRAFT')}</label>
                    <input
                      className="font-black text-primary bg-transparent outline-none py-1 w-full text-[11px]"
                      value={data.target_craft || ''}
                      placeholder="e.g., MECHANICAL"
                      disabled={isReadOnly}
                      onChange={e => setData({...data, target_craft: e.target.value})}
                    />
                  </div>

                  <StaticField label={t('common.org')} value={data.org_id} />
                  <StaticField label={t('common.site')} value={data.site_id} />
                  <CustomFieldSlot coreModelName="SR" tab="MAIN" section="Logistics" recordId={data.srnum} disabled={isNew} />

                  <div className="bg-primary-soft p-4 rounded-md border border-primary/20 shadow-inner mt-4 flex items-start gap-3">
                    <Info size={18} className="text-primary mt-0.5 shrink-0" />
                    <p className="text-[10px] text-primary font-bold leading-tight italic">
                      {t('sr.details.wf_tip', 'Automated Workflow: After saving, use the "Route Workflow" button to send this request for technical review.')}
                    </p>
                  </div>
                </div>
              </SectionCard>
            </div>
          </div>
        )}
        
        {/* Related Records Tab */}
        {activeTab === 'RELATED' && (
          <div className="max-w-7xl mx-auto animate-in slide-in-from-right duration-500 pb-20">
            <SectionCard title={`${t('sr.related.title')} ${data.srnum}`} isRTL={isRTL}>
              <div className="overflow-x-auto">
                <table className={`w-full border-collapse ${isRTL ? 'text-right' : 'text-left'}`}>
                  <thead className="bg-surface-panel border-b font-black text-ink-secondary uppercase tracking-widest">
                    <tr>
                      <th className="p-4">{t('sr.related.wonum', 'WO Number')}</th>
                      <th className="p-4">{t('sr.fields.description', 'Description')}</th>
                      <th className="p-4">{t('common.status', 'Status')}</th>
                      <th className="p-4">{t('sr.related.worktype', 'Work Type')}</th>
                      <th className="p-4 text-center">{t('common.actions', 'Action')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-line-subtle">
                    {relatedWO.length === 0 ? (
                      <tr>
                        <td colSpan="5" className="p-12 text-center text-ink-secondary italic">
                          <div className="flex flex-col items-center gap-2">
                            <List size={32} className="opacity-20" />
                            {t('sr.related.no_wo', 'No Work Orders have been created for this SR yet.')}
                          </div>
                        </td>
                      </tr>
                    ) : (
                      relatedWO.map((wo) => (
                        <tr key={wo.wonum} className="hover:bg-primary-soft transition-colors group">
                          <td className="p-4 font-black text-primary">{wo.wonum}</td>
                          <td className="p-4 font-bold text-ink">{wo.description}</td>
                          <td className="p-4">
                            <span className="px-3 py-1 bg-primary-soft text-primary rounded-full text-[9px] font-black uppercase shadow-sm border border-primary/20">
                              {wo.status}
                            </span>
                          </td>
                          <td className="p-4 font-black text-ink-secondary text-[10px]">{wo.worktype || 'CM'}</td>
                          <td className="p-4 text-center">
                            <button
                              onClick={() => navigate(`/workorders/${wo.wonum}`)}
                              className="p-2 text-primary hover:bg-primary hover:text-white rounded transition-all"
                              title={t('sr.related.go_to_wo')}
                            >
                              <Zap size={14} />
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </SectionCard>
          </div>
        )}
      </div>

      {/* Modals & Popups */}
      {showStatusModal && <StatusModal appName="SR" recordId={data.srnum} currentStatus={data.status} onClose={() => setShowStatusModal(false)} onComplete={loadData} />}
      {showHistoryModal && <StatusHistoryModal appName="SR" recordId={data.srnum} onClose={() => setShowHistoryModal(false)} />}
      {isDocModalOpen && <AttachmentsModal isOpen={isDocModalOpen} onClose={() => {setIsDocModalOpen(false); fetchCount();}} ownertable="SR" ownerid={data.srnum} />}
      <AuditHistoryModal isOpen={showAudit} onClose={() => setShowAudit(false)} tableName="sr" recordId={data.srnum} />
      {isWFHistoryOpen && <WorkflowHistoryModal module="SR" refId={id} onClose={() => setIsWFHistoryOpen(false)} />}
    </div>
  );
};

// --- المكونات المساعدة ---
const HeaderField = ({ label, value, onChange, width, isBold, disabled, isRTL }) => (
  <div className={`flex flex-col px-4 last:border-0 border-line-subtle ${isRTL ? 'border-l' : 'border-r'}`}>
    <label className="text-[8px] text-ink-secondary font-black uppercase tracking-widest mb-0.5 italic leading-none">{label}</label>
    <input className={`${width} border-b border-transparent outline-none py-0.5 font-black uppercase ${isBold ? 'text-primary text-[12px]' : 'text-primary'} bg-transparent transition-all focus:border-primary`}
           value={value || ''} disabled={disabled} onChange={e => onChange && onChange(e.target.value)} />
  </div>
);

const SectionCard = ({ title, children, isRTL }) => (
  <div className="bg-surface-card border rounded shadow-sm overflow-hidden transition-all hover:shadow-md">
    <div className={`p-2 bg-surface-panel border-b font-black text-ink-secondary uppercase text-[9px] tracking-widest flex items-center gap-2 italic ${isRTL ? 'flex-row-reverse' : ''}`}>
      <div className="w-1.5 h-3 bg-primary-dark rounded-full"></div> {title}
    </div>
    <div className="p-5">{children}</div>
  </div>
);

const EditableField = ({ label, value, onChange, onSearch, icon, disabled, type = "text", isRTL }) => (
  <div className="flex flex-col gap-1 border-b border-line group focus-within:border-primary transition-all py-1 px-1">
    <label className="text-ink-secondary font-black text-[8px] uppercase tracking-widest italic leading-none">{label}</label>
    <div className={`flex items-center justify-between pb-0.5 h-5 ${isRTL ? 'flex-row-reverse' : ''}`}>
      <div className={`flex items-center gap-2 w-full ${isRTL ? 'flex-row-reverse' : ''}`}>
        {icon && <span className="text-ink-secondary">{icon}</span>}
        <input type={type} className={`outline-none font-black text-primary bg-transparent w-full text-[11px] uppercase placeholder:text-ink-secondary ${isRTL ? 'text-right' : 'text-left'}`}
          placeholder="ENTER DATA..."
          value={value || ''} disabled={disabled} onChange={e => onChange && onChange(e.target.value)} />
      </div>
      {onSearch && <Search size={12} className="text-primary cursor-pointer hover:text-primary-dark transition-colors" onClick={onSearch} />}
    </div>
  </div>
);

const StaticField = ({ label, value }) => (
  <div className="flex flex-col border-b border-line-subtle py-1.5 px-1">
    <label className="text-[8px] font-black text-ink-secondary uppercase tracking-widest italic">{label}</label>
    <div className="font-black uppercase tracking-tighter text-primary text-[10px] mt-1">{value || '---'}</div>
  </div>
);

export default SRDetails;